import Vue from 'vue';
import axios from 'axios';

const state = {
  userInfo: null,
  internalUserId: null,
  verificationStatus: null,
  authStatus: false,
  settings: {
    hiddenCollections: [],
    pinnedCards: [],
    pinnedNotes: [],
    pinnedItems: [],
  },
  quickNotes: null,
  etag: null,
};

const getters = {
  isAuthenticated: state => true,
  userId: state => state.internalUserId || localStorage.getItem('userId'),
};

const actions = {
  registerUser({ commit }, userData) {
    return new Promise((resolve, reject) => {
      // Some validation
      if (!userData.email || !userData.auth0UserId || !userData.accountRole || !userData.collectionAccessLevel || !userData.planLevel) {
        reject(new Error('Missing entries'));
        return;
      }
      // Validation passed
      axios.post('user', userData)
        .then((res) => {
          if (res.data && res.data.errorMessage && res.data.errorMessage.includes('ER_DUP_ENTRY')) {
            // User already existed, reject and stop execution
            const error = new Error('User already exists');
            reject(error);
            return;
          }
          const { userId } = res.data;
          commit('SetInternalUserId', { internalUserId: userId });
          resolve(userData);
        })
        .catch((err) => {
          // No error handling necessary apart from rejecting (registering errors handled in RegisterUser.vue)
          reject(err);
        });
    });
  },
  getDataAtSetup({ dispatch, commit }) {
    return new Promise((resolve, reject) => {
      axios.get('user/1/data')
        .then((res) => {
          if (!res) {
            console.error('Error: got no data!', res);
          }
          else {
            commit('SetETag', res.headers.etag);

            const { userInfo, collections, cards, notes } = res.data;

            commit('SetInternalUserId', { internalUserId: userInfo.userId });
            commit('SetVerificationStatus', { verificationStatus: res.data.verificationStatus });

            dispatch('collections/setCollections', collections, { root: true });
            commit('cards/SetCards', cards, { root: true });
            commit('notes/SetNotes', notes, { root: true });
            commit('SetUserInfo', userInfo);

            // This is for setting the pinnedCards and pinnedNotes in appState, for the openCards/openNotes lists
            const { settings } = userInfo;
            if (settings) {
              const { pinnedCards, pinnedNotes, pinnedItems } = settings;

              // Also use pinnedCards and pinnedNotes that are still stored in the db (for now, is depreciated with merging notes and cards in the same view)
              const allPinnedItems = pinnedItems || [];
              pinnedCards.forEach(cardId => allPinnedItems.push({ id: cardId, type: 'card' }));
              pinnedNotes.forEach(noteId => allPinnedItems.push({ id: noteId, type: 'note' }));

              dispatch('appState/setPinnedItems', allPinnedItems, { root: true });
            }

            commit('appState/InitialDataLoaded', null, { root: true });

            resolve({
              userId: userInfo.userId,
              verificationStatus: res.data.verificationStatus,
            });
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  getDataForPolling({ state, dispatch, commit }) {
    return new Promise((resolve, reject) => {
      axios.get('user/1/data') // same data as getDataAtStartup, we just do not set internalUserId, verificationStatus, and initialDataLoaded
        .then((res) => {
          if (!res) {
            console.error('Error: got no data!', res);
          }
          else if (res.headers.etag === state.etag) {
            // Content not modified.
            resolve();
          }
          else {
            commit('SetETag', res.headers.etag);

            const { userInfo, collections, cards, notes } = res.data;

            dispatch('collections/setCollections', collections, { root: true });
            commit('cards/SetCards', cards, { root: true });
            commit('notes/SetNotes', notes, { root: true });
            commit('SetUserInfo', userInfo);

            // This is for setting the pinnedCards and pinnedNotes in appState, for the openCards/openNotes lists
            const { settings } = userInfo;
            if (settings) {
              const { pinnedCards, pinnedNotes, pinnedItems } = settings;

              // Also use pinnedCards and pinnedNotes that are still stored in the db (for now, is depreciated with merging notes and cards in the same view)
              const allPinnedItems = pinnedItems || [];
              pinnedCards.forEach(cardId => allPinnedItems.push({ id: cardId, type: 'card' }));
              pinnedNotes.forEach(noteId => allPinnedItems.push({ id: noteId, type: 'note' }));

              dispatch('appState/setPinnedItems', allPinnedItems, { root: true });
            }

            resolve();
          }
        })
        .catch((err) => {
          reject(err);
        });
    });
  },
  updateUser({ dispatch, commit }, userData) {
    return new Promise((resolve, reject) => {
      // Some validation
      if (!Object.keys(userData).length) {
        reject(new Error('No updates specified'));
        return;
      }

      commit('SetUserInfo', userData);
      dispatch('sync/enqueueUserUpdate', userData, { root: true });
      resolve();
    });
  },
  deleteUser({ dispatch, state }, deleteUserObj) {
    return new Promise((resolve, reject) => {
      // Currently no validation, also not used
      const config = {
        data: deleteUserObj,
      };
      axios.delete(`user/${state.userInfo.userId}`, config)
        .then(() => {
          this.$auth.logout();
          resolve();
        })
        .catch((err) => {
          if (err.message === 'Network Error') {
            const customMessage = 'Could not save changes due to a problem with the network. Please check your internet connection.';
            dispatch('notifications/setError', { error: err, customMessage }, { root: true });
          }
          else dispatch('notifications/setError', { error: err }, { root: true });
          reject();
        });
    });
  },
  updateHiddenCollections({ state, dispatch, commit }, hiddenCollections) {
    commit('UpdateHiddenCollections', hiddenCollections);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  updatePinnedItems({ state, dispatch, commit }, pinnedItems) {
    const pinnedItemsSetting = [];
    pinnedItems.forEach((pinnedItem) => {
      pinnedItemsSetting.push({
        type: pinnedItem.type,
        id: pinnedItem.ref.id,
      });
    });
    commit('UpdatePinnedItems', pinnedItemsSetting);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  pinItem({ state, commit, dispatch }, item) {
    commit('PinItem', item);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  unpinItem({ state, commit, dispatch }, item) {
    commit('UnpinItem', item);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  pinCard({ state, commit, dispatch }, cardId) {
    commit('PinCard', cardId);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  unpinCard({ state, commit, dispatch }, cardId) {
    commit('UnpinCard', cardId);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  pinNote({ state, commit, dispatch }, noteId) {
    commit('PinNote', noteId);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
  unpinNote({ state, commit, dispatch }, noteId) {
    commit('UnpinNote', noteId);
    dispatch('sync/enqueueUserUpdate', { settings: state.settings }, { root: true });
  },
};

const mutations = {
  SetAuthStatus(state, status) {
    state.authStatus = status;
  },
  SetETag(state, etag) {
    state.etag = etag;
  },
  SetUserInfo(state, userInfo) {
    const { settings, quickNotes, ...info } = userInfo;
    state.userInfo = Object.assign(state.userInfo || {}, info);
    state.userInfo.updateMessageSeen = Number(state.userInfo.updateMessageSeen) || 0;
    // Quick notes
    if (quickNotes) state.quickNotes = quickNotes;
    // New accounts may not have a settings object yet in the db
    if (settings) {
      Object.keys(settings).forEach((setting) => {
        Vue.set(state.settings, setting, settings[setting]);
      });
    }
  },
  SetInternalUserId(state, { internalUserId }) {
    state.internalUserId = internalUserId;
  },
  SetVerificationStatus(state, { verificationStatus }) {
    state.verificationStatus = verificationStatus;
  },
  UpdateHiddenCollections(state, hiddenCollections) {
    state.settings.hiddenCollections = hiddenCollections;
  },
  UpdatePinnedItems(state, pinnedItems) {
    state.settings.pinnedItems = pinnedItems;
  },
  PinItem(state, { id, type }) {
    state.settings.pinnedItems.push({ id, type });
  },
  UnpinItem(state, { id, type }) {
    const index = state.settings.pinnedItems.findIndex(item => item.id === id && item.type === type);
    if (index >= 0) Vue.delete(state.settings.pinnedItems, index);
  },
  PinCard(state, cardId) {
    state.settings.pinnedCards.push(cardId);
  },
  UnpinCard(state, cardId) {
    const index = state.settings.pinnedCards.findIndex(id => id === cardId);
    if (index >= 0) Vue.delete(state.settings.pinnedCards, index);
  },
  PinNote(state, noteId) {
    state.settings.pinnedNotes.push(noteId);
  },
  UnpinNote(state, noteId) {
    const index = state.settings.pinnedNotes.findIndex(id => id === noteId);
    if (index >= 0) Vue.delete(state.settings.pinnedNotes, index);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
